import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Controller, useFormContext } from 'react-hook-form';
import { IconButton, InputAdornment, MenuItem, Stack, TextField } from '@mui/material';
import { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify/Iconify';

import { PROG_YN_ALL } from './FilterSection';

interface KeywordPortalProps {
  onSubmit?: () => void;
}

// 헤더 셀렉트 옵션: 폼 상태(progYnList 배열)와 단일 문자열 간 매핑
const PROG_YN_SELECT_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: '진행중', value: 'true' },
  { label: '종료', value: 'false' },
];

/**
 * 목록 화면에 Portal로 렌더링되는 검색 필드 (진행중 여부 + 키워드)
 * SearchFilter가 닫혀있어도 항상 렌더링되며, Drawer 내 필터와 동일한 form 필드를
 * 사용하므로 양쪽 값이 자동으로 동기화된다.
 */
export const KeywordPortal: React.FC<KeywordPortalProps> = ({ onSubmit }) => {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const { watch, setValue, control } = useFormContext();
  const textValue = watch('text');

  // Portal 대상 요소가 DOM에 마운트될 때까지 기다림
  useEffect(() => {
    const checkPortalTarget = () => {
      const target = document.getElementById('keyword-search-portal');
      if (target) {
        setPortalTarget(target);
      } else {
        // 아직 DOM에 없으면 다음 틱에 다시 확인
        setTimeout(checkPortalTarget, 10);
      }
    };

    checkPortalTarget();
  }, []);

  if (!portalTarget) return null;

  return ReactDOM.createPortal(
    <Stack direction="row" gap={1} alignItems="center">
      {/* 진행중 여부 - 선택 즉시 조회 */}
      <Controller
        name="progYnList"
        control={control}
        render={({ field }) => {
          // 배열(폼 상태) -> 셀렉트 단일값. 하나만 선택된 경우가 아니면 '전체'
          const selected = Array.isArray(field.value) ? field.value : [];
          const value = selected.length === 1 ? String(selected[0]) : 'all';

          return (
            <TextField
              select
              size="small"
              label="진행중 여부"
              value={value}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v === 'all' ? PROG_YN_ALL : [v]);
                onSubmit && onSubmit();
              }}
              sx={{ width: 130 }}
            >
              {PROG_YN_SELECT_OPTIONS.map((v) => (
                <MenuItem key={`progYn-${v.value}`} value={v.value}>
                  {v.label}
                </MenuItem>
              ))}
            </TextField>
          );
        }}
      />

      {/* 키워드 - Enter 시 조회 */}
      <RHFTextField
        name="text"
        label=""
        placeholder="키워드 검색(Enter)"
        size="small"
        sx={{ width: 250 }}
        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter' && onSubmit) {
            e.preventDefault();
            onSubmit();
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon={'eva:search-fill'} />
            </InputAdornment>
          ),
          endAdornment: textValue && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setValue('text', '');
                  onSubmit && onSubmit();
                }}
                edge="end"
              >
                <Iconify icon={'eva:close-fill'} width={18} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Stack>,
    portalTarget,
  );
};
